import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  PlusSquareOutlined,
} from "@ant-design/icons";
import {
  Button,
  Popover,
  Space,
  Table,
  TableColumnsType,
  TablePaginationConfig,
} from "antd";
import {
  RequestComplement,
  ResponseEntryDetail,
  ResponseInputRegisterMovements,
} from "api/generated";
import useLoggedUser from "hooks/useLoggedUser";
import moment from "moment";
import { Link } from "react-router-dom";
import { EVM_PERMISSION, getPagePermissions } from "security/permissions";
import PgElimination from "./pgElimination";
import { UsePaginationTableReturn } from "hooks/commons/usePaginationTable";
import { Dispatch, SetStateAction, useMemo } from "react";
import { UseTableSelectionReturn } from "hooks/commons/useTableSelection";
import { FilterValue, SorterResult } from "antd/es/table/interface";

interface ReceptionTableProps {
  selection: UseTableSelectionReturn;
  isLoading: boolean;
  reloadTable: () => void;
  pagination: UsePaginationTableReturn<ResponseEntryDetail>;
  setFilterCfdi: Dispatch<SetStateAction<boolean | undefined>>;
  deleteComplement: (record: RequestComplement) => void;
  goToEditComplement: (record: ResponseEntryDetail) => void;
}

function ReceptionTable({
  selection,
  isLoading,
  reloadTable,
  pagination,
  setFilterCfdi,
  deleteComplement,
  goToEditComplement,
}: ReceptionTableProps) {
  const { permissions } = useLoggedUser();
  const pagePermissions = getPagePermissions(EVM_PERMISSION, permissions);

  const receptions = useMemo(() => pagination.items, [pagination.items]);

  function handleOnDelete(record: ResponseEntryDetail) {
    deleteComplement({
      //General info
      receptionUUID: record.id ?? "",
      transactionType: "compra",
      cfdiComplementRegisterType: undefined, //complementToDelete.movementType,

      //CFDI Complement Data
      isCfdiComplement: false,
      currentcfdiComplementUUID: record.cfdiComplement?.id,

      //Transport Complement Data
      isTransportComplement: false,
      transportComplementUUID: record.transportComplement?.id,

      //Storage Complement Data
      isStorageComplement: false,
      storageComplementUUID: record.storageComplement?.id,

      //International Complement Data
      isInternationalComplement: false,
      internationalComplementUUID: record.internationalComplement?.id,

      delete: true,
    });
  }

  const columns: TableColumnsType<ResponseEntryDetail> = [
    {
      title: "Fecha de inicio",
      dataIndex: "inputRegisterReceptionDate",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
    {
      title: "Fecha de fin",
      dataIndex: "inputRegisterEndReceptionDate",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
      sorter: true,
    },
    { title: "Equipo", dataIndex: "inputRegisterEquipmentCode" },
    { title: "Origen", dataIndex: "pgVehicle" },
    {
      title: "Número de registro",
      dataIndex: "inputRegisterFolio",
      sorter: true,
    },
    {
      title: "Volumen",
      dataIndex: "inputRegisterVolume",
      render: (inputRegisterVolume) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          inputRegisterVolume
        ),
    },
    {
      title: "Masa",
      dataIndex: "inputRegisterMass",
      render: (inputRegisterMass) =>
        Intl.NumberFormat("en", { notation: "standard" }).format(
          inputRegisterMass
        ),
    },
    { title: "Almacén", dataIndex: "inputRegisterWarehouseKey" },
    { title: "Serie", dataIndex: "invoiceSerie" /* sorter: true  */ },
    { title: "Folio", dataIndex: "invoiceFolio" /* sorter: true */ },
    {
      title: "CFDI",
      dataIndex: "fiscalId",
      filters: [
        { text: "Facturados", value: true },
        { text: "Sin CFDI", value: false },
      ],
      render: (text) => (text ? text.toUpperCase() : ""),
    },
    {
      title: "PGs",
      align: "center",
      render: (_text, record) =>
        record.isEditAllowed && record.inputRegisterAssociatedPg !== null ? (
          <div style={{ display: "inline-block" }}>
            <Space.Compact>
              {pagePermissions.edit && (
                <Link
                  key="2"
                  to={{
                    pathname: `/receptions/components/pgRegistration/${record.inputRegisterAssociatedPg?.id}/${record.id}`,
                  }}
                >
                  <Popover content="Editar PG">
                    <Button shape="circle" icon={<EditOutlined />} />
                  </Popover>
                </Link>
              )}
              {pagePermissions.delete && (
                <PgElimination
                  inputRegisterAssociatedPg={record.inputRegisterAssociatedPg}
                  handleSubmit={() => {
                    reloadTable();
                  }}
                />
              )}
            </Space.Compact>
          </div>
        ) : record.isEditAllowed &&
          record.inputRegisterAssociatedPg === null ? (
          <div>
            {pagePermissions.add && (
              <Link
                key="1"
                to={{
                  pathname: `/receptions/components/pgRegistration/0/${record.id}`,
                }}
              >
                <Popover content="Agregar PG">
                  <Button type="primary" icon={<PlusOutlined />} />
                </Popover>
              </Link>
            )}
          </div>
        ) : record.isEditAllowed === false &&
          record.inputRegisterAssociatedPg ? (
          <div>
            {pagePermissions.read && (
              <Link
                key="3"
                to={{
                  pathname: `/receptions/components/pgRegistration/${record.inputRegisterAssociatedPg.id}/${record.id}`,
                }}
              >
                <Popover content="Ver PG">
                  <Button icon={<EyeOutlined />} />
                </Popover>
              </Link>
            )}
          </div>
        ) : (
          <div>{pagePermissions.read && <span>Sin PG asociado</span>}</div>
        ),
    },
    {
      title: "Complemento",
      dataIndex: "complement",
      render: (_, record) => {
        const isEditable = record.isEditAllowed;
        const hasComplements =
          record.cfdiComplement ||
          record.storageComplement ||
          record.transportComplement ||
          record.internationalComplement ||
          record.clarifications;

        if (isEditable && !hasComplements) {
          return (
            <Popover content="Agregar información para complemento">
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  goToEditComplement(record);
                }}
              />
            </Popover>
          );
        }

        if (isEditable && hasComplements) {
          return (
            <Space>
              <Popover content="Editar información de complemento">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => {
                    goToEditComplement(record);
                  }}
                />
              </Popover>
              <Popover content="Eliminar información de complemento">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => {
                    handleOnDelete(record);
                  }}
                />
              </Popover>
            </Space>
          );
        }

        if (!isEditable && hasComplements) {
          return (
            <Popover content="Ver información de complemento">
              <Button
                icon={<EyeOutlined />}
                onClick={() => {
                  goToEditComplement(record);
                }}
              />
            </Popover>
          );
        }

        return <span>Sin complemento asociado</span>;
      },
    },
  ];

  const columnsInside: TableColumnsType<ResponseInputRegisterMovements> = [
    {
      title: "Volumen",
      dataIndex: "rmVolume",
      width: "20%",
      render: (text) => `${text}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    },
    {
      title: "Masa",
      dataIndex: "rmMass",
      width: "20%",
      render: (text) => `${text}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
    },
    {
      title: "Folio del Movimiento",
      dataIndex: "rmFolio",
      width: "20%",
    },
    {
      title: "Fecha de inicio del movimiento",
      dataIndex: "rmStartDate",
      width: "20%",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
    {
      title: "Fecha de fin del movimiento",
      dataIndex: "rmEndDate",
      width: "20%",
      render: (date) => moment(date).format("YYYY/MM/DD HH:mm:ss"),
    },
  ];

  function handleTableChange(
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<ResponseEntryDetail>
      | SorterResult<ResponseEntryDetail>[]
  ) {
    // Manejar filtros
    const fiscalIdFilter = filters.fiscalId;
    if (fiscalIdFilter && fiscalIdFilter.length === 1) {
      const filterCfdi = fiscalIdFilter[0] as boolean;
      setFilterCfdi(filterCfdi);
    } else {
      setFilterCfdi(undefined);
    }

    // Manejar ordenamiento
    if (!Array.isArray(sorter) && sorter.field && sorter.order) {
      const { field, order } = sorter;
      const direction = order === "ascend" ? "ASC" : "DESC";

      // Mapeo de campos de ordenamiento
      const sortMapping: Record<string, string> = {
        inputRegisterReceptionDate: "initialDate",
        inputRegisterEndReceptionDate: "endDate",
        inputRegisterFolio: "folio",
        invoiceSerie: "invoiceSerie",
        invoiceFolio: "invoiceFolio",
      };

      const sortField = sortMapping[field as string] || "endDate";

      pagination.setSort([`${sortField},${direction}`]);
    } else {
      // Reset al ordenamiento por defecto si no hay ordenamiento activo
      pagination.setSort(["endDate,DESC"]);
    }
  }

  return (
    <Table
      title={() => (
        <span style={{ marginLeft: "10px" }}>
          Presione <PlusSquareOutlined /> para ver movimientos de la recepción
        </span>
      )}
      rowSelection={selection.rowSelection}
      rowKey="id"
      style={{ width: "100%" }}
      columns={columns}
      dataSource={receptions}
      expandable={{
        expandedRowRender: (r) => (
          <Table
            className="nested-table"
            columns={columnsInside}
            dataSource={r.inputRegisterMovements}
            pagination={false}
            rowKey="rmFolio"
          />
        ),
      }}
      loading={isLoading}
      pagination={{
        ...pagination.paginationConfig,
        onChange: pagination.onPageChange,
        onShowSizeChange: pagination.onPageSizeChange,
      }}
      onChange={handleTableChange}
      scroll={{
        x: 1500,
        y: "calc(100vh - 600px)",
      }}
    />
  );
}

export default ReceptionTable;
